import { useMemo, useState } from "react";
import BarChart from "@/components/Chart/BarChart";
import DonutChart from "@/components/Chart/DonutChart";
import LineChart from "@/components/Chart/LineChart";
import SegmentedButton from "@/components/SegmentedButton";
import Select from "@/components/Select";
import { PieChartSkeleton } from "@/components/Skeleton";
import { sysColors } from "@/assets/theme/colors";
import { ChartCard, EmptyChartMessage } from "../../_components/ChartShells";
import type { CallTopicDay, CallTopicStat } from "../queries";
import {
  buildSubtopicMetricData,
  buildTopicBarData,
  buildTrendData,
  colorForTopic,
  formatCount,
  formatDuration,
  formatPercent,
  formatTotalDuration,
  percentOf,
  subtopicMetricLabel,
  type SubtopicMetric
} from "../_utils";

type Props = {
  topics: CallTopicStat[];
  days: CallTopicDay[];
  totalCalls: number;
  analyzedCalls: number;
  drilldownTopic: string;
  drilldownOptions: { value: string }[];
  onDrilldownTopicChange: (topic: string) => void;
  loading?: boolean;
};

export default function TopicCharts({
  topics,
  days,
  totalCalls,
  analyzedCalls,
  drilldownTopic,
  drilldownOptions,
  onDrilldownTopicChange,
  loading = false
}: Props) {
  const topicBarData = useMemo(() => buildTopicBarData(topics), [topics]);
  const topTopics = useMemo(() => topicBarData.map((row) => row.name), [topicBarData]);
  const selectedTopics = useMemo(() => topics.map((bucket) => bucket.topic), [topics]);
  const trend = useMemo(() => buildTrendData(days, selectedTopics, topTopics), [days, selectedTopics, topTopics]);
  const drilldownBucket = useMemo(
    () => topics.find((bucket) => bucket.topic === drilldownTopic) ?? null,
    [topics, drilldownTopic]
  );
  const [subtopicMetric, setSubtopicMetric] = useState<SubtopicMetric>("calls");
  const subtopicMetricData = useMemo(
    () => buildSubtopicMetricData(drilldownBucket, subtopicMetric),
    [drilldownBucket, subtopicMetric]
  );
  const coverage = percentOf(analyzedCalls, totalCalls);
  // Vertical bars need ~30px per row; clamp so full and filtered states both breathe.
  const topicChartHeight = Math.min(460, Math.max(225, topicBarData.length * 30 + 60));
  const subtopicChartHeight = Math.min(460, Math.max(225, subtopicMetricData.length * 30 + 60));

  return (
    <div className="grid gap-4 md:grid-cols-2 mt-8" data-testid="call-topic-charts">
      <BarChart
        data={topicBarData}
        bars={[{ dataKey: "Calls", name: "Calls", color: sysColors["sys-brand-primary"] }]}
        title="Calls by topic"
        subtitle="Completed and unclassified calls in range."
        loading={loading}
        layout="vertical"
        height={topicChartHeight}
        showValueLabels
        emptyLabel="No calls match these filters."
      />

      {!loading && trend.rows.length === 0 ? (
        <ChartCard title="Daily call volume" testId="call-topic-trend-chart">
          <EmptyChartMessage label="No calls match these filters." />
        </ChartCard>
      ) : (
        <LineChart
          data={trend.rows}
          lines={trend.series}
          title="Daily call volume"
          subtitle="Total plus the top topics in range."
          loading={loading}
        />
      )}

      {loading ? (
        <ChartCard title="Analysis coverage" testId="call-topic-coverage-chart">
          <PieChartSkeleton />
        </ChartCard>
      ) : totalCalls === 0 ? (
        <ChartCard title="Analysis coverage" testId="call-topic-coverage-chart">
          <EmptyChartMessage label="No calls in this range" />
        </ChartCard>
      ) : (
        <div data-testid="call-topic-coverage-chart" className="h-full min-w-0">
          <DonutChart
            data={[{ name: "Analyzed", value: analyzedCalls }]}
            total={totalCalls}
            title="Analysis coverage"
            label="Analyzed calls"
            displayValue={formatPercent(coverage)}
            description={`${formatCount(analyzedCalls)} of ${formatCount(totalCalls)} calls classified.`}
          />
        </div>
      )}

      <ChartCard title="Subtopic breakdown" testId="call-topic-subtopic-chart">
        <Select
          label="Topic"
          value={drilldownTopic}
          options={drilldownOptions}
          onSelect={onDrilldownTopicChange}
          data-testid="call-topic-drilldown-select"
          searchable
          placeholder="Select topic"
        />
        {loading ? (
          <EmptyChartMessage label="Loading subtopics..." />
        ) : subtopicMetricData.length === 0 || !drilldownBucket ? (
          <EmptyChartMessage label="No classified calls for this topic" />
        ) : (
          <>
            <p
              className="text-body-medium text-sys-brand-on-surface-variant m-0"
              data-testid="call-topic-drilldown-stats"
            >
              Avg {formatDuration(drilldownBucket.avgDurationSeconds)} · Total{" "}
              {formatTotalDuration(drilldownBucket.totalDurationSeconds)} across {formatCount(drilldownBucket.count)}{" "}
              calls
            </p>
            <SegmentedButton
              options={[
                { label: "Calls", value: "calls" },
                { label: "Avg duration", value: "avgDuration" },
                { label: "Total time", value: "totalTime" }
              ]}
              value={subtopicMetric}
              onChange={(value) => {
                if (value) setSubtopicMetric(value as SubtopicMetric);
              }}
              ariaLabel="Subtopic metric"
            />
            <BarChart
              data={subtopicMetricData}
              bars={[
                { dataKey: "value", name: subtopicMetricLabel(subtopicMetric), color: colorForTopic(drilldownTopic) }
              ]}
              title={`${drilldownTopic} subtopics`}
              layout="vertical"
              height={subtopicChartHeight}
              bordered={false}
              showValueLabels
            />
          </>
        )}
      </ChartCard>
    </div>
  );
}
